<template>
  <div v-if="pageContent" class="electricity-usage">
    <h1 class="electricity-usage__title h4">
      {{ pageContent.title }}
    </h1>
    <p class="electricity-usage__description">
      {{ pageContent.description }}
    </p>
    <RadioGroup
      v-model="estimatedUsage"
      class="electricity-usage__options"
      :options="usageOptions"
    />
    <AppButton
      class="electricity-usage__next-button"
      :disabled="!estimatedUsage"
      full-width
      @click="$emit('go-forward')"
    >
      <span>{{ nextStepButtonCopy }}</span>
      <span class="icon icon-arrow-right"></span>
    </AppButton>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue"

import AppButton from "@soenergy/frontend-library/src/components/AppButton.vue"
import RadioGroup from "@soenergy/frontend-library/src/components/RadioButton/RadioGroup.vue"
import cmsPreviewMixin from "@soenergy/frontend-library/src/mixins/cmsPreviewMixin"
import story from "soenergy-cms-loader!?path=solar/usage-estimate"
// from the bands module rather than the engine barrel: the barrel imports the
// 43KB generated reckoner table, and this step is routed at every flag value,
// so going through it would pin the table into the main bundle chunk
import {
  BAND_RANGE_LABELS,
  BAND_TITLES,
  BAND_USAGE_KWH,
} from "@/services/solarEstimateEngine/bands"

export interface SolarQuoteElectricityUsageEstimate {
  title: string
  description: string
  nextStepButtonCopy: string
  usage_options: SolarQuoteElectricityUsageOption[]
}

export interface SolarQuoteElectricityUsageOption {
  title: string
  value: string
}

// In code rather than CMS (deprecated). Labels show the band so a customer can
// place their usage; the committed value is what the estimate is modelled on.
const BEDROOM_HINTS: Record<string, string> = {
  low: "1-2 bedrooms/flat",
  med: "3-4 bedrooms",
  high: "5+ bedrooms",
}

// the band picker feeds straight into the estimate, so the CTA names that
// destination. Matches QuoteElectricityUsage and QuoteElectricityUnitRate.
const RECKONER_NEXT_STEP_BUTTON_COPY = "Calculate My Estimate Price"

const RECKONER_USAGE_OPTIONS: SolarQuoteElectricityUsageOption[] = (
  ["low", "med", "high"] as const
).map((band) => ({
  title: `${BAND_TITLES[band]}: ${BAND_RANGE_LABELS[band]} (${BEDROOM_HINTS[band]})`,
  value: `Estimated ${BAND_USAGE_KWH[band]}`,
}))

export default defineComponent({
  components: {
    AppButton,
    RadioGroup,
  },
  mixins: [cmsPreviewMixin<SolarQuoteElectricityUsageEstimate>({ story })],
  emits: ["go-forward"],
  computed: {
    nextStepButtonCopy(): string {
      return process.env.VUE_APP_ENABLE_QUOTE_RECKONER === "true"
        ? RECKONER_NEXT_STEP_BUTTON_COPY
        : this.pageContent.nextStepButtonCopy
    },
    usageOptions() {
      const options =
        process.env.VUE_APP_ENABLE_QUOTE_RECKONER === "true"
          ? RECKONER_USAGE_OPTIONS
          : this.pageContent.usage_options
      return options.map((option) => ({
        label: option.title,
        value: option.value,
        inputId: `input-${option.value}`,
      }))
    },
    estimatedUsage: {
      get() {
        return this.$store.state.quote.estimatedUsage.value
      },
      set(value) {
        this.$store.commit("SET_ESTIMATED_USAGE", { value })
      },
    },
  },
})
</script>

<style lang="scss" scoped>
.electricity-usage {
  &__title {
    text-transform: none;
  }

  &__description {
    font-size: $font-size-4;
    margin-top: $space-6;

    @include md {
      margin-top: $space-10;
    }
  }

  &__options {
    margin-top: $space-6;

    :deep(.radio-button__input:checked + .radio-button__label),
    :deep(.radio-button__input:focus + .radio-button__label) {
      .radio-button__text {
        font-weight: $weight-normal;
        text-shadow: 0.01em 0 0 currentColor, -0.01em 0 0 currentColor;
      }
    }
  }

  &__next-button {
    margin-top: $space-3;
  }
}
</style>
